import { CookieManager } from "./cookie-manager"
import { FronteggService } from "./frontegg-service"

class SessionManager {
  #defaultTimeout = 15;
  #warningMinsBeforeLogout = 2;
  constructor(options) {
    this.postLogoutRedirectURI = options.postLogoutRedirectURI;
    this.onLogout = options.onLogout || function () { };
    this.onLogout = options.onLogout || function () { };
    this.logoutTimeout = null;
    this.logoutWarningTimeout = null;
    this.app = null;
    this.token = null;
    this.sessionTimeout = this.#defaultTimeout;
    this.domain = options.domain || this.resolveSharedDomain();
    this.cookieManager = new CookieManager({ isDevelopment: options.isDevelopment, domain: this.domain });
    this.logger = options.logger || false;
    this.loginUrl = options.loginUrl;
  }

  run(options) {
    if (typeof options === "undefined") {
      options = {}
    }

    this.app = options.app;

    if(!this.app) {
      console.error('App not provided');
      return;
    }

    this.onAccessTokenChange = options.onAccessTokenChange || function () { };
    this.onExpirationWarning = options.onExpirationWarning || function () { };
    this.onRefreshExpiration = options.onRefreshExpiration || function () {};
    const onUnAuthenticated = options.onUnAuthenticated || function () { };

    this.checkSessionExpired()

    const onInit = this.onInit.bind(this);
    const onAuthenticated = this.onAuthenticated.bind(this);
    const subscribeToAuth = function(){
      const fronteggService = new FronteggService({ app: this.app });
      fronteggService.subscribeToAuth(onInit, onAuthenticated, onUnAuthenticated)
    }.bind(this);

    subscribeToAuth();
  }

  onInit() {
    if (this.cookieManager.retrieve('session_logout')) {
      this.cookieManager.remove('session_logout');
    }
  }

  onAuthenticated(authData) {
    if (!this.token) {
      this.setupSessionWatchers();
    }
    if (this.token != authData.user.accessToken) {
      if (this.logger) {
        console.log('There is a new token');
      }
      this.token = authData.user.accessToken;
      this.onAccessTokenChange(this.token);
      this.setSessionTimeout(this.app.store.getState().auth.tenantsState.activeTenant);
      this.checkForActiveSession();
    }
  }

  setupSessionWatchers() {
    setInterval(() => {
      if (this.cookieManager.retrieve('session_logout')) {
        if (this.logger) {
          console.log('Another app logged out');
        }
        this.logout()
      }
    }, 5000);

    // we will check on session expiration if the user had activity and warn it 2mins before
    this.scheduleLogout(this.expiresIn())
    this.scheduleLogoutWarning(this.warnExpirationIn())
    this.updateExpiresAt();
  }

  logout() {
    this.onLogout();
    if (!this.cookieManager.retrieve('session_logout')) {
      this.cookieManager.store('session_logout', true);
    }
    this.cookieManager.remove('session_expires_at')

    // this.app.logout(); // TODO: bug in Frontegg that doesn't allow to use it in Firefox, so instead we should use the next

    const baseUrl = this.loginUrl || this.app.store.getState().root.context.baseUrl;
    const url = new URL(`${baseUrl}/oauth/logout`);
    let options = { post_logout_redirect_uri: this.redirectToUri(this.postLogoutRedirectURI) }
    url.search = new URLSearchParams(options);
    window.location.href = url.toString();
  }

  redirectToUri(path) {
    return `${window.location.origin}${path}`;
  }

  scheduleLogout(expiresInSeconds) {
    clearTimeout(this.logoutTimeout);
    let checkSessionExpired = this.checkSessionExpired.bind(this);
    this.logoutTimeout = setTimeout(function () {
      checkSessionExpired()
    }, expiresInSeconds * 1000);
  }

  checkForActiveSession() {
    let active = function () {
      this.updateExpiresAt();
      this.scheduleLogout(this.expiresIn())
      this.scheduleLogoutWarning(this.warnExpirationIn())
    }
    const refreshFrequency = 60 * 1000;
    document.addEventListener('mousemove', this.throttle(active.bind(this), refreshFrequency));
  }

  scheduleLogoutWarning(expiresInSeconds) {
    clearTimeout(this.logoutWarningTimeout);
    let cookieManager = this.cookieManager;
    let onExpirationWarning = this.onExpirationWarning;
    let warningMinsBeforeLogout = this.#warningMinsBeforeLogout;
    this.logoutWarningTimeout = setTimeout(function () {
      const expiresAt = cookieManager.retrieve('session_expires_at');
      if (expiresAt && (new Date(Date.parse(expiresAt) - 1000 * 60 * warningMinsBeforeLogout) < new Date())) {
        onExpirationWarning()
      }
    }, expiresInSeconds * 1000);
  }

  calculateExpiresAt(sessionTimeout) {
    return new Date((new Date()).getTime() + sessionTimeout * 60 * 1000)
  }

  setSessionTimeout(organization) {
    try {
      const originalTimeout = this.sessionTimeout;
      if (!organization) {
        if (this.logger) {
          console.error('Could not find organization');
        }
        return;
      }
      this.sessionTimeout = JSON.parse(organization.metadata).sessionTimeoutInMinutes;
      if (this.sessionTimeout !== originalTimeout) {
        this.updateExpiresAt();
        if (this.logger) {
          console.log('Session timeout is', this.sessionTimeout);
        }
      }
    } catch (e) {
      if (this.logger) {
        console.error('Could not set session timeout ', e);
      }
    }
  }

  checkSessionExpired() {
    const expiresAt = this.cookieManager.retrieve('session_expires_at');
    if (expiresAt && (new Date(Date.parse(expiresAt)) < new Date())) {
      if (this.logger) {
        console.log('Session expired, logging out');
      }
      this.cookieManager.remove('session_expires_at')
      this.logout();
    }
  }

  throttle(func, timeFrame) {
    var lastTime = 0;
    return function () {
      var now = Date.now();
      if (now - lastTime >= timeFrame) {
        func();
        lastTime = now;
      }
    };
  }

  expiresIn() {
    return this.sessionTimeout * 60;
  }

  warnExpirationIn() {
    return (this.sessionTimeout - this.#warningMinsBeforeLogout) * 60;
  }

  updateExpiresAt() {
    if (this.logger) {
      console.log('Refreshing expires at');
    }

    this.cookieManager.store('session_expires_at', this.calculateExpiresAt(this.sessionTimeout));
    this.onRefreshExpiration();
  }

  resolveSharedDomain() {
    const url = window.location.hostname;
    const rootDomainRegex = /(\.[^.]+){2,}$/;

    return url.match(rootDomainRegex) ? url.match(rootDomainRegex)[0] : url; // should output ".yourdomain.com"
  }
}

export default SessionManager
